<template>
  <div class="h-full flex gap-6">
    <!-- Left Side: Filter Content -->
    <div class="w-[400px] flex-shrink-0 pl-6 py-6 flex flex-col">
      <!-- Fixed Size Components -->
      <DateSelector v-model="selectedDate" @dateSelected="selectDate" class="flex-shrink-0" />

      <div class="bg-white rounded-lg shadow p-4 flex-shrink-0 mt-4">
        <h3 class="text-sm font-medium text-gray-700 mb-4">유형</h3>
        <BondTypeConditionComponent @bondGroupsUpdated="handleBondGroupsUpdate" />
      </div>

      <div class="bg-white rounded-lg shadow p-4 flex-shrink-0 mt-4">
        <h3 class="text-sm font-medium text-gray-700 mb-4">만기</h3>
        <MaturityConditionComponent v-model="selectedMaturityRange" />
      </div>

      <!-- Graphs Section -->
      <div v-if="hasEnoughHeight" class="mt-4 lg:block" :class="{ hidden: showMobileTable }">
        <div class="bg-white rounded-lg shadow p-4">
          <!-- 그래프 컨텐츠 -->
          <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <div class="flex flex-wrap items-center gap-2">
              <button v-for="type in chartTypes" :key="type.id" @click="selectedChartType = type.id" :class="[
                'px-3 py-1.5 text-sm rounded-md',
                selectedChartType === type.id
                  ? 'bg-gray-700 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
              ]">
                {{ type.name }}
              </button>
            </div>

            <div v-if="selectedChartType === 'ktb'" class="flex items-center">
              <select v-model="selectedKtbType"
                class="text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option v-for="type in ktbTypes" :key="type.id" :value="type.id">
                  {{ type.name }}
                </option>
              </select>
            </div>
          </div>
          <div class="h-[300px]">
            <!-- 구간별 체결강도 차트 -->
            <CreditGraphComponent v-if="selectedChartType === 'credit' && bondType.bondTypeId" :key="refreshKey"
              :asks="filteredByTypeAsks" :bondType="bondType" class="w-full h-full"
              @update:maturityRange="handleMaturityRangeUpdate" />

            <!-- KTB 스프레드 차트 -->
            <KtbYieldBandChart v-if="selectedChartType === 'ktb' && ktbTransactions.length > 0"
              :transactions="ktbTransactions" />

            <!-- 안내 메시지 -->
            <div v-if="!bondType.bondTypeId" class="flex items-center justify-center h-full text-gray-400 text-sm">
              채권 유형을 선택해주세요
            </div>
          </div>
        </div>
      </div>

      <!-- 그래프 토글 버튼 (높이가 부족할 때) -->
      <div v-else class="mt-4">
        <button @click="toggleGraphModal"
          class="w-full bg-white rounded-lg shadow p-4 text-sm text-gray-700 hover:bg-gray-50">
          그래프 보기
        </button>
      </div>
    </div>

    <!-- Mobile Toggle Button -->
    <div class="fixed bottom-4 right-4 lg:hidden z-50">
      <button @click="showMobileTable = !showMobileTable" class="bg-blue-600 text-white rounded-full p-3 shadow-lg">
        <i :class="showMobileTable ? 'fas fa-chart-line' : 'fas fa-table'" class="w-6 h-6"></i>
      </button>
    </div>

    <!-- Right Side: Table Content (높이 기준) -->
    <div ref="rightContent" class="flex-1 pr-6 py-6">
      <div class="bg-white rounded-lg shadow h-full flex flex-col overflow-hidden">
        <CreditSellAskContainerComponent v-if="bondType.bondTypeId" v-model="filteredByTypeAsks"
          :maturityRange="selectedMaturityRange" v-model:searchKeyword="searchKeyword" :isLoading="isLoading"
          @refresh="refreshData">
        </CreditSellAskContainerComponent>
        <div v-else class="h-full flex items-center justify-center text-gray-500">
          채권 유형을 선택해주세요
        </div>
      </div>
    </div>

    <!-- 그래프 모달 -->
    <div v-if="showGraphModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-3xl">
        <div class="p-4 border-b border-gray-200 flex justify-between items-center">
          <h3 class="text-lg font-medium">그래프</h3>
          <button @click="showGraphModal = false" class="text-gray-500 hover:text-gray-700">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="p-4">
          <!-- 그래프 컨텐츠 복사 -->
          <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <div class="flex flex-wrap items-center gap-2">
              <button v-for="type in chartTypes" :key="type.id" @click="selectedChartType = type.id" :class="[
                'px-3 py-1.5 text-sm rounded-md',
                selectedChartType === type.id
                  ? 'bg-gray-700 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
              ]">
                {{ type.name }}
              </button>
            </div>

            <div v-if="selectedChartType === 'ktb'" class="flex items-center">
              <select v-model="selectedKtbType"
                class="text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option v-for="type in ktbTypes" :key="type.id" :value="type.id">
                  {{ type.name }}
                </option>
              </select>
            </div>
          </div>
          <div class="h-[550px]">
            <!-- 모달에서는 더 큰 높이 사용 -->
            <!-- 구간별 체결강도 차트 -->
            <CreditGraphComponent v-if="selectedChartType === 'credit' && bondType.bondTypeId" :key="refreshKey"
              :asks="filteredByTypeAsks" :bondType="bondType" class="w-full h-full"
              @update:maturityRange="handleMaturityRangeUpdate" />

            <!-- KTB 스프레드 차트 -->
            <KtbYieldBandChart v-if="selectedChartType === 'ktb' && ktbTransactions.length > 0"
              :transactions="ktbTransactions" />

            <!-- 안내 메시지 -->
            <div v-if="!bondType.bondTypeId" class="flex items-center justify-center h-full text-gray-400 text-sm">
              채권 유형을 선택해주세요
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import BondTypeConditionComponent from '@/components/conditions/BondTypeConditionComponent.vue';
import MaturityConditionComponent from '@/components/conditions/MaturityConditionComponent.vue';
import CreditGraphComponent from '@/components/CreditGraphComponent.vue';
import CreditSellAskContainerComponent from '@/components/CreditAskResultComponent.vue';
import askService from '@/service/creditQuoteService';
import DateSelector from '@/components/NavigationDateSelector.vue';
import { getTodayStr } from '@/utils/util';
import ktbService from '@/service/ktbService';
import KtbYieldBandChart from '@/components/ktb/KtbYieldBandChart.vue';
import userService from '@/service/userService';
import { useStore } from 'vuex';

export default {
  name: 'SellView',
  components: {
    BondTypeConditionComponent,
    MaturityConditionComponent,
    CreditGraphComponent,
    CreditSellAskContainerComponent,
    DateSelector,
    KtbYieldBandChart,
  },
  setup() {
    const store = useStore();

    // State
    const selectedDate = ref(getTodayStr());
    const allOnSaleAsks = ref([]);
    const allSoldOutAsks = ref([]);
    const selectedMaturityRange = ref([0, 61]);
    const searchKeyword = ref('');
    const selectedGroup = ref('');
    const isLoading = ref(false);
    const errorMessage = ref(null);
    const isDateDropdownOpen = ref(false);
    const refreshKey = ref(0);
    // 로컬스토리지에서 저장된 값 불러오기
    const savedChartType = localStorage.getItem('sellView_chartType') || 'credit';
    const savedKtbType = localStorage.getItem('sellView_ktbType') || 'Y03';

    const selectedKtbType = ref(savedKtbType);
    const ktbTypes = [
      { id: 'Y02', name: '2년' },
      { id: 'Y03', name: '3년' },
      { id: 'Y05', name: '5년' },
      { id: 'Y10', name: '10년' },
      { id: 'Y20', name: '20년' },
      { id: 'Y30', name: '30년' },
    ];
    const ktbTransactions = ref([]);
    const isDebouncing = ref(false);
    const DEBOUNCE_DELAY = 1000; // 1초
    const selectedChartType = ref(savedChartType);
    const chartTypes = [
      { id: 'credit', name: '구간별 체결강도' },
      { id: 'ktb', name: '국고채 추이' },
    ];
    const showMobileTable = ref(true);
    const isLargeScreen = ref(window.innerWidth >= 1024);
    const rightContent = ref(null);
    const showGraphModal = ref(false);
    const hasEnoughHeight = ref(true);

    // 페이지 가시성 관련 변수들
    let healthCheckInterval = null;
    const HEALTH_CHECK_INTERVAL = 5000; // 5초

    // Computed Properties
    const bondType = computed(() => store.state.filter.bondType);
    const bondGroups = computed(() => store.state.filter.bondGroups);

    // Fetch Data
    const fetchData = async () => {
      if (!bondType.value.bondTypeId) return;

      try {
        isLoading.value = true;
        const [askData] = await Promise.all([
          askService.findCreditSell(
            selectedDate.value,
            bondType.value.bondTypeId,
            {
              minMonths: selectedMaturityRange.value[0],
              maxMonths: selectedMaturityRange.value[1],
              searchKeyword: searchKeyword.value,
            }
          ),
          fetchKtbData(),
        ]);

        const { onSaleQuotes, soldOutQuotes } = askData;
        allOnSaleAsks.value = onSaleQuotes;
        allSoldOutAsks.value = soldOutQuotes;
        refreshKey.value += 1;
      } catch (error) {
        console.error('Error fetching data:', error);
        errorMessage.value = '데이터를 불러오는 중 오류가 발생했습니다.';
      } finally {
        isLoading.value = false;
      }
    };

    // KTB 데이터만 따로 조회하는 함수
    const fetchKtbData = async () => {
      try {
        const data = await ktbService.findDaily(
          selectedKtbType.value,
          selectedDate.value
        );
        ktbTransactions.value = data;
      } catch (error) {
        console.error('Error fetching KTB data:', error);
        ktbTransactions.value = [];
      }
    };

    // 날짜 선택 핸들러
    const selectDate = date => {
      selectedDate.value = date;
      if (bondType.value.bondTypeId) {
        fetchData();
      }
    };

    // 날짜 포맷 함수
    const formatDateButton = dateStr => {
      const date = new Date(dateStr);
      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${month}/${day}`;
    };

    // Refresh data without changing state
    const refreshData = async () => {
      if (isLoading.value || isDebouncing.value) return;

      isDebouncing.value = true;
      await fetchData();

      setTimeout(() => {
        isDebouncing.value = false;
      }, DEBOUNCE_DELAY);
    };

    // 그룹 선택 처리
    const selectBondGroup = group => {
      if (selectedGroup.value === group.id) {
        selectedGroup.value = '';
        store.dispatch('filter/setBondType', {
          ...bondType.value,
          filterType: 'group',
          filterId: null,
          issuerIds: null,
        });
      } else {
        selectedGroup.value = group.id;
        store.dispatch('filter/setBondType', {
          ...bondType.value,
          filterType: 'group',
          filterId: group.id,
          issuerIds: group.issuerIds,
        });
      }
    };

    const getGroupName = groupId => {
      const group = bondGroups.value.find(g => g.id === groupId);
      return group ? group.name : '';
    };

    // 타입(그룹/등급)에 의한 1차 필터링
    const filteredByTypeAsks = computed(() => {
      // 매도중인 호가와 체결된 호가를 합쳐서 필터링
      let filteredOnSale = allOnSaleAsks.value;
      let filteredSoldOut = allSoldOutAsks.value;

      if (
        bondType.value.filterMode === 'group' &&
        bondType.value.selectedGroupId
      ) {
        const group = bondGroups.value.find(
          g => g.id === bondType.value.selectedGroupId
        );
        if (group && group.issuerIds) {
          filteredOnSale = filteredOnSale.filter(ask =>
            group.issuerIds.includes(ask.bondIssuerId)
          );
          filteredSoldOut = filteredSoldOut.filter(ask =>
            group.issuerIds.includes(ask.bondIssuerId)
          );
        }
      } else if (bondType.value.filterMode === 'rating') {
        if (!bondType.value.selectedRatings?.length) {
          filteredOnSale = [];
          filteredSoldOut = [];
        } else {
          filteredOnSale = filteredOnSale.filter(ask =>
            bondType.value.selectedRatings.includes(ask.grade)
          );
          filteredSoldOut = filteredSoldOut.filter(ask =>
            bondType.value.selectedRatings.includes(ask.grade)
          );
        }
      }

      // 합쳐서 반환 (CreditAskResultComponent에서 다시 분리할 예정)
      return [...filteredOnSale, ...filteredSoldOut];
    });

    // Watchers
    watch(
      () => bondType.value.bondTypeId,
      async newId => {
        if (newId) {
          await fetchData();
        } else {
          allOnSaleAsks.value = [];
          allSoldOutAsks.value = [];
        }
      }
    );

    // KTB 타입 변경 시 데이터 재조회 및 로컬스토리지 저장
    watch(selectedKtbType, (newValue) => {
      localStorage.setItem('sellView_ktbType', newValue);
      fetchKtbData();
    });

    // 차트 타입 변경 시 로컬스토리지 저장
    watch(selectedChartType, (newValue) => {
      localStorage.setItem('sellView_chartType', newValue);
    });

    // 날짜 변경 시에도 KTB 데이터 재조회
    watch(selectedDate, () => {
      fetchKtbData();
    });

    // bondGroups 업데이트 핸들러 수정
    const handleBondGroupsUpdate = groups => {
      store.dispatch('filter/setBondGroups', groups);
    };

    const formatDateDisplay = dateStr => {
      const date = new Date(dateStr);
      const today = new Date();
      const isToday = date.toDateString() === today.toDateString();

      const month = date.getMonth() + 1;
      const day = date.getDate();
      const dayOfWeek = new Intl.DateTimeFormat('ko-KR', {
        weekday: 'short',
      }).format(date);

      return `${month}월 ${day}일 (${dayOfWeek})${isToday ? ' 오늘' : ''}`;
    };

    // 화면 크기 변경 감지
    onMounted(() => {
      const handleResize = () => {
        isLargeScreen.value = window.innerWidth >= 1024;
        if (isLargeScreen.value) {
          showMobileTable.value = true;
        }
      };

      window.addEventListener('resize', handleResize);
      handleResize();

      onUnmounted(() => {
        window.removeEventListener('resize', handleResize);
      });

      // 페이지 가시성 변경 이벤트 리스너 등록
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // 초기 시작
      if (!document.hidden) {
        startHealthCheck();
      }

      checkHeight();
      window.addEventListener('resize', checkHeight);

      // 컴포넌트 마운트 시 채권 유형 초기화
      store.dispatch('filter/setBondType', {
        bondTypeId: null,
        filterMode: null,
        selectedGroupId: null,
        selectedRatings: [],
        issuerIds: null,
      });
    });

    // 페이지 가시성 변경 감지 핸들러
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // 페이지가 숨겨질 때 인터벌 중지
        if (healthCheckInterval) {
          clearInterval(healthCheckInterval);
          healthCheckInterval = null;
        }
      } else {
        // 페이지가 보일 때 인터벌 시작
        startHealthCheck();
      }
    };

    // health check 시작 함수
    const startHealthCheck = () => {
      if (healthCheckInterval) return;

      // 즉시 한 번 실행
      sendHealthCheck();

      // 이후 주기적으로 실행
      healthCheckInterval = setInterval(() => {
        sendHealthCheck();
      }, HEALTH_CHECK_INTERVAL);
    };

    // health check 전송 함수
    const sendHealthCheck = async () => {
      if (!document.hidden) {
        try {
          const currentPath = window.location.pathname;
          await userService.sendHealthCheck(currentPath);
        } catch (error) {
          console.error('Health check 전송 실패:', error);
        }
      }
    };

    onUnmounted(() => {
      // 이벤트 리스너 제거 및 인터벌 정리
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (healthCheckInterval) {
        clearInterval(healthCheckInterval);
      }
    });

    // 그래프 표시 여부 계산
    const checkHeight = () => {
      if (!rightContent.value) return;
      const rightHeight = rightContent.value.clientHeight;
      // 필요한 최소 높이 계산 (DateSelector + 유형 + 만기 + 그래프 높이)
      const requiredHeight = 700; // 예상 최소 높이 (실제 값으로 조정 필요)
      hasEnoughHeight.value = rightHeight >= requiredHeight;
    };

    const toggleGraphModal = () => {
      showGraphModal.value = true;
    };

    return {
      // State
      allOnSaleAsks,
      allSoldOutAsks,
      selectedMaturityRange,
      searchKeyword,
      selectedGroup,
      selectedDate,

      // Computed
      bondType,
      bondGroups,
      filteredByTypeAsks,

      // Methods
      handleMaturityRangeUpdate: range => {
        selectedMaturityRange.value = range;
      },
      selectBondGroup,
      getGroupName,
      isLoading,
      errorMessage,
      refreshData,
      handleBondGroupsUpdate,
      selectDate,
      formatDateButton,
      isDateDropdownOpen,
      formatDateDisplay,
      refreshKey,
      selectedKtbType,
      ktbTypes,
      ktbTransactions,
      isDebouncing,
      selectedChartType,
      chartTypes,
      showMobileTable,
      isLargeScreen,
      hasEnoughHeight,
      showGraphModal,
      toggleGraphModal,
      rightContent,
    };
  },
};
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1024px) {
  .animate-spin {
    animation: spin 1s linear infinite;
  }
}
</style>
